/**
 * @author Nihar <nihar.baishyar@314ecorp.com>
 * @description utils for entity settings
 */

import _ from 'lodash';
import { RowDragEndEvent } from 'ag-grid-community';
import { trimValue } from './providers';

// system defined id prefix
const SYSTEM_DEFINED_ID_PREFIX = '00000000-0000-0000-0000';

// worklist settings form keys
const WORKLIST_SETTINGS_FORM_KEYS: Record<
	keyof EntitySettings.IWorklistSettingsFormModel,
	keyof EntitySettings.IWorklistSettingsFormModel
> = {
	tableMeta: 'tableMeta',
};

// get last node of the tree
const getLeafPairs = (data: EntitySettings.SettingsNode[] = []): { key: string; value: string }[] => {
	const dataNodes = [] as { key: string; value: string }[];
	const getDataNodes = (childData: EntitySettings.SettingsNode[] = []) => {
		_.forEach(childData, (child) => {
			if (_.isEmpty(child?.children) && child?.key) {
				dataNodes.push({ key: child.key, value: child?.value?.value });
				return;
			}
			getDataNodes(child.children);
		});
	};
	getDataNodes(data?.[0]?.children || []); // start from children of first data item
	return dataNodes;
};

// initial model for worklist settings
const worklistSettingsInitialModel = (tableMetadata: EntitySettings.IPatchModelItem[] = []): EntitySettings.IWorklistSettingsFormModel => {
	return {
		tableMeta: tableMetadata,
	};
};

// patch model for worklist settings
const worklistSettingsPatchModel = (
	initialModel: EntitySettings.IWorklistSettingsFormModel,
	currentModel: EntitySettings.IWorklistSettingsFormModel,
): EntitySettings.IPatchModelItem[] => {
	const initial = initialModel?.tableMeta || [];
	const current = currentModel?.tableMeta || [];

	const initialByKey = _.keyBy(initial, (item) => item?.key);

	// return items whose value changed
	return _.filter(current, (item) => {
		const prev = initialByKey?.[item.key];
		return !_.isEqual(prev?.value, item?.value);
	});
};

// get reorder

const getReorderPayload = (
	rowData: EntitySettings.IWriteOffReasonResponse[] | ClaimRequest.IActivityType[],
	event: RowDragEndEvent<EntitySettings.IWriteOffReasonResponse> | RowDragEndEvent<ClaimRequest.IActivityType>,
	systemActivityTypes: ClaimRequest.IActivityType[] | EntitySettings.IWriteOffReasonResponse[],
): { id: string; oldOrder: number; newOrder: number }[] => {
	const oldOrderItems = _.cloneDeep(rowData);
	const newOrderItems = _.map(event?.node?.parent?.childrenAfterSort, (node) => node?.data);

	// Check if order changed
	const oldOrderIds = _.map(oldOrderItems, (item) => item?.id);
	const newOrderIds = _.map(newOrderItems, (item) => item?.id);
	if (_.isEqual(oldOrderIds, newOrderIds)) {
		return;
	}
	// Prepare payload for reorder API
	const payload = [];
	_.map(_.cloneDeep(newOrderItems), (item, index) => {
		const oldOrder = _.find(oldOrderItems, { id: item?.id });
		const newOrder = index + 1;
		if (oldOrder?.order !== newOrder) {
			payload.push({ id: item.id, oldOrder: oldOrder?.order, newOrder });
		}
	});
	_.map(_.cloneDeep(systemActivityTypes), (item, index) => {
		const oldOrder = _.find(systemActivityTypes, { id: item?.id });
		const newOrder = _.size(rowData) + index + 1;
		if (oldOrder?.order !== newOrder) {
			payload.push({ id: item.id, oldOrder: oldOrder?.order, newOrder });
		}
	});
	return payload;
};
// check if entity name is duplicate
const isDuplicateName = ({
	initialName,
	currentName,
	allNames,
}: {
	initialName: string;
	currentName: string;
	allNames: string[];
}): boolean => {
	const trimmedNames = _.map(allNames, (item) => trimValue(item));
	if (_.isEmpty(currentName)) {
		return false;
	}
	return initialName !== currentName && _.includes(trimmedNames, trimValue(currentName));
};

export {
	WORKLIST_SETTINGS_FORM_KEYS,
	SYSTEM_DEFINED_ID_PREFIX,
	isDuplicateName,
	getReorderPayload,
	getLeafPairs,
	worklistSettingsInitialModel,
	worklistSettingsPatchModel,
};
